import type { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Dialog } from '@components/dialog/Dialog';
import { Form, FormSection } from '@components/forms';
import { SubjectivityItem, useUpdateSubjectivity } from '@services/subjectivity';
import { FormFieldType, TFormField } from '@models/forms';
import {
  SUPPRESS_POLICY_GENERATED_EMAIL_LABEL,
  SuppressPolicyGeneratedEmailLabel,
} from '@components/Subjectivities/SuppressPolicyGeneratedEmailCheckbox';

export interface MarkSubjectivityCompleteDialogProps {
  isOpen: boolean;
  subjectivity: SubjectivityItem;
  transactionNumber: string;
  onClose: () => void;
  /**
   * When true, shows a checkbox allowing the user to suppress the policy package generated email that is
   * sent once all subjectivities are complete. Only relevant when completing the last open subjectivity.
   */
  showSuppressEmailOption?: boolean;
}

interface FormValues {
  reason?: string;
  suppressEmail?: boolean;
}

export const MarkSubjectivityCompleteDialog: FC<MarkSubjectivityCompleteDialogProps> = ({
  isOpen,
  subjectivity,
  transactionNumber,
  onClose,
  showSuppressEmailOption = false,
}) => {
  const { updateSubjectivity, isPending } = useUpdateSubjectivity();

  const methods = useForm<FormValues>({
    defaultValues: { suppressEmail: false },
    mode: 'onChange',
  });

  const { handleSubmit } = methods;

  const fields: { [fieldName: string]: TFormField } = {
    reason: {
      name: 'reason',
      fieldType: FormFieldType.TextField,
      label: 'Reason',
      placeholder: 'Optional reason for marking as complete',
      gridWidth: { xs: 12 },
      fieldConfig: {
        textField: {
          multiline: true,
          rows: 4,
          maxLength: 1000,
        },
      },
    },
    suppressEmail: {
      name: 'suppressEmail',
      fieldType: FormFieldType.CheckboxField,
      label: SUPPRESS_POLICY_GENERATED_EMAIL_LABEL,
      defaultValue: false,
      isVisible: showSuppressEmailOption,
      disabled: isPending,
      gridWidth: { xs: 12 },
      sx: { alignItems: 'flex-start' },
      fieldConfig: {
        checkboxField: {
          CustomLabelComponent: <SuppressPolicyGeneratedEmailLabel />,
        },
      },
    },
  };

  const onSubmit = async (data: FormValues) => {
    await updateSubjectivity({
      subjectivityId: subjectivity.id,
      bypassed: true,
      bypassReason: data.reason ?? undefined,
      ...(showSuppressEmailOption
        ? { suppressSubjectivityCompletePolicyGeneratedEmail: data.suppressEmail ?? false }
        : {}),
    });

    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      size='medium'
      title={`Mark Subjectivity as Complete on #${transactionNumber}`}
      handleCloseDialog={onClose}
      primaryBtnText='Mark as Complete'
      primaryBtnAction={handleSubmit(onSubmit)}
      isPrimaryBtnDisabled={isPending}
      isPrimaryBtnLoading={isPending}
      secondaryBtnText='Exit'
      secondaryBtnAction={onClose}
      isSecondaryBtnDisabled={isPending}
    >
      <Box sx={{ marginY: 2, marginX: 3 }}>
        <Typography variant='bodySmall' mb={2} fontWeight='bold' sx={{ display: 'block', color: 'text.secondary' }}>
          Subjectivity: {subjectivity.label}
        </Typography>

        <Typography variant='bodySmall' sx={{ color: 'text.secondary' }}>
          Mark a subjectivity as complete to bypass the document requirement - either because it is not needed, or it is
          uploaded elsewhere on the quote or policy.
        </Typography>

        <Form methods={methods} formId='mark-subjectivity-complete-form'>
          <Box sx={{}}>
            <FormSection fields={fields} styleConfig={{ spacing: 1 }} />
          </Box>
        </Form>
      </Box>
    </Dialog>
  );
};
